// Twee versies van dezelfde notitie samenvoegen.
//
// Het geval: je typt op de telefoon iets bij terwijl de laptop nog een oudere kopie had, en
// die schrijft daarna ook. De server ziet dan een botsing. Tot nu toe maakte de app er een
// conflictkopie van — niets kwijt, maar wel twee notities waar je er één wilde.
//
// Hier staat de rekensom die dat oplost: wat veranderde ík ten opzichte van de gezamenlijke
// basis, en wat veranderde het andere apparaat? Raakten we verschillende stukken, dan kunnen
// beide wijzigingen gewoon naast elkaar bestaan. Raakten we hetzelfde stuk anders, dan valt
// er niets te kiezen zonder iets weg te gooien — en dan houden we het bij de conflictkopie.
//
// De eenheid is één blok: een alinea, een kop, een lijst, een codeblok. Fijner (per woord)
// zou slimmer lijken maar levert half samengevoegde zinnen op; per blok is wat je in een
// notitie ook zelf zou doen.

import type { Blok } from "./api.ts";

export type TekstSamenvoeging = { tekst: string; schoon: boolean };
export type Samenvoeging = { doc: Blok; schoon: boolean };

const LEEG: Blok = { type: "doc", content: [{ type: "paragraph" }] };

export function voegTekstSamen(basis: string, mijn: string, hun: string): TekstSamenvoeging {
  if (mijn === hun) return { tekst: mijn, schoon: true };
  if (mijn === basis) return { tekst: hun, schoon: true };
  if (hun === basis) return { tekst: mijn, schoon: true };
  return { tekst: mijn, schoon: false };
}

function blokkenVan(doc: Blok | null | undefined): Blok[] {
  return doc?.content ?? [];
}

function sleutel(blok: Blok): string {
  return JSON.stringify(blok);
}

/**
 * Welke posities in `a` en `b` dezelfde regel bevatten, in dezelfde volgorde: de langste
 * gemeenschappelijke rij. Hierop ankeren we het samenvoegen.
 */
function ankers(a: string[], b: string[]): Array<[number, number]> {
  const tabel: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      tabel[i][j] = a[i] === b[j] ? tabel[i + 1][j + 1] + 1 : Math.max(tabel[i + 1][j], tabel[i][j + 1]);
    }
  }
  const paren: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      paren.push([i, j]);
      i++;
      j++;
    } else if (tabel[i + 1][j] >= tabel[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }
  return paren;
}

function gelijk(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((waarde, i) => waarde === b[i]);
}

export function voegSamen(basis: Blok | null, mijn: Blok | null, hun: Blok | null): Samenvoeging {
  const basisBlokken = blokkenVan(basis);
  const mijnBlokken = blokkenVan(mijn);
  const hunBlokken = blokkenVan(hun);

  const basisSleutels = basisBlokken.map(sleutel);
  const mijnSleutels = mijnBlokken.map(sleutel);
  const hunSleutels = hunBlokken.map(sleutel);

  if (gelijk(mijnSleutels, hunSleutels)) return { doc: mijn ?? LEEG, schoon: true };
  if (gelijk(mijnSleutels, basisSleutels)) return { doc: hun ?? LEEG, schoon: true };
  if (gelijk(hunSleutels, basisSleutels)) return { doc: mijn ?? LEEG, schoon: true };

  // Alleen de blokken die ná de wijzigingen van beide kanten nog op hun plek staan, zijn een
  // betrouwbaar ankerpunt. Daartussen kijken we per stuk wie er iets veranderde.
  const metMij = new Map(ankers(basisSleutels, mijnSleutels));
  const metHun = new Map(ankers(basisSleutels, hunSleutels));
  const vaste: Array<{ basis: number; mijn: number; hun: number }> = [];
  for (let i = 0; i < basisSleutels.length; i++) {
    const inMijn = metMij.get(i);
    const inHun = metHun.get(i);
    if (inMijn !== undefined && inHun !== undefined) vaste.push({ basis: i, mijn: inMijn, hun: inHun });
  }

  const uit: Blok[] = [];
  let schoon = true;
  let basisPlek = 0;
  let mijnPlek = 0;
  let hunPlek = 0;

  const stukje = (tot: { basis: number; mijn: number; hun: number }) => {
    const basisStuk = basisSleutels.slice(basisPlek, tot.basis);
    const mijnStuk = mijnSleutels.slice(mijnPlek, tot.mijn);
    const hunStuk = hunSleutels.slice(hunPlek, tot.hun);

    if (gelijk(mijnStuk, hunStuk)) {
      uit.push(...mijnBlokken.slice(mijnPlek, tot.mijn));
    } else if (gelijk(mijnStuk, basisStuk)) {
      uit.push(...hunBlokken.slice(hunPlek, tot.hun)); // alleen zij veranderden hier iets
    } else if (gelijk(hunStuk, basisStuk)) {
      uit.push(...mijnBlokken.slice(mijnPlek, tot.mijn)); // alleen ik
    } else {
      // Allebei, en anders. Hier valt niets te kiezen zonder iemand tekort te doen.
      schoon = false;
      uit.push(...mijnBlokken.slice(mijnPlek, tot.mijn), ...hunBlokken.slice(hunPlek, tot.hun));
    }
  };

  for (const anker of vaste) {
    stukje(anker);
    uit.push(basisBlokken[anker.basis]);
    basisPlek = anker.basis + 1;
    mijnPlek = anker.mijn + 1;
    hunPlek = anker.hun + 1;
  }
  stukje({ basis: basisSleutels.length, mijn: mijnSleutels.length, hun: hunSleutels.length });

  return { doc: { type: "doc", content: uit.length > 0 ? uit : [{ type: "paragraph" }] }, schoon };
}
